import angular from 'angular';
import { ACCESS_TYPES } from '../../../account/common/services/access-types';
import * as call from '../../../modules/call';
import * as devices from '../../../modules/devices';
import * as localMedia from '../../../modules/local-media';
import { AudioTypes } from '../../../providers/video/abstract-video-provider';
import { EndCallReasons } from '../../services/doxy-analytics/tracks/video-session-end';
import { RootScopeEvents } from '../../services/root-scope-types';
import template from './video-settings.html';
import './video-settings.scss';
class VideoSettingsController {
    constructor(VideoProviderFactory, $element, $scope, $rootScope, $timeout, videoSettingsHelper, currentUserHelper, communicationControl, trackingFactory, VideoQualityService, notificationFactory, setSinkId, Browser, User, $ngRedux, $translate, selfMicrophoneRenderer) {
        this.VideoProviderFactory = VideoProviderFactory;
        this.$element = $element;
        this.$scope = $scope;
        this.$rootScope = $rootScope;
        this.$timeout = $timeout;
        this.videoSettingsHelper = videoSettingsHelper;
        this.currentUserHelper = currentUserHelper;
        this.communicationControl = communicationControl;
        this.trackingFactory = trackingFactory;
        this.VideoQualityService = VideoQualityService;
        this.notificationFactory = notificationFactory;
        this.setSinkId = setSinkId;
        this.Browser = Browser;
        this.User = User;
        this.$ngRedux = $ngRedux;
        this.$translate = $translate;
        this.selfMicrophoneRenderer = selfMicrophoneRenderer;
        this.qualityOptions = [];
        this.isSpeakersTesting = false;
        this.isVideoPlaying = false;
        this.onAudioTypeChange = this.handleAudioTypeChange.bind(this);
        this.unsubscribeFromStore = this.$ngRedux.connect(this.mapStateToThis.bind(this))(this);
        this.audioTypeOptions = [
            {
                value: AudioTypes.STANDARD,
                label: $translate.instant('video-settings.audio-type.standard'),
            },
            {
                value: AudioTypes.MEDICAL,
                label: $translate.instant('video-settings.audio-type.medical'),
                infoLabel: $translate.instant('video-settings.audio-type-info'),
            },
        ];
    }
    $onInit() {
        for (const [quality, label] of Object.entries(this.VideoQualityService.options)) {
            this.qualityOptions.push({
                quality: quality,
                label,
            });
        }
        const videoEl = this.$element[0].querySelector('video');
        videoEl.volume = 0;
        videoEl.addEventListener('playing', () => {
            void this.$timeout(() => {
                this.isVideoPlaying = true;
            });
        });
        videoEl.addEventListener('suspend', () => {
            void this.$timeout(() => {
                this.isVideoPlaying = false;
            });
        });
        this.$scope.$watch(() => this.VideoProviderFactory.activeProvider.localStream, () => {
            videoEl.srcObject =
                this.VideoProviderFactory.activeProvider.localStream;
        });
        this.selfMicrophoneRenderer.initCanvasContexts();
    }
    $onDestroy() {
        this.unsubscribeFromStore();
    }
    changeMic(device) {
        this.$ngRedux.dispatch(call.actions.changeMic(device.deviceId));
    }
    changeCamera(device) {
        this.$ngRedux.dispatch(call.actions.changeCamera(device.deviceId));
    }
    changeSpeaker(device) {
        this.$rootScope.$emit(RootScopeEvents.USER_ACTIONS.CHANGE_SPEAKER);
        if (!this.setSinkId.isSupported) {
            this.notificationFactory.showCommon('warning', 'speakers-selection-unsupported', null, 10000);
            return;
        }
        this.$ngRedux.dispatch(devices.actions.changeOutputDevice(device.deviceId));
    }
    changeQuality({ quality }) {
        if (!Boolean(this.currentUserHelper.canUsePaidFeatures())) {
            this.trackingFactory.global.track('Modal Opened', {
                plan_type: undefined,
                modal_type: 'notification bar',
                session_id: this.VideoProviderFactory.activeProvider.callSession.id,
                modal_trigger: 'upgrade video quality',
            });
            if (quality === call.constants.VideoQuality.Medium) {
                this.notificationFactory.showCommon('warning', 'upgrade.videoQualitySD');
            }
            else if (quality === call.constants.VideoQuality.High) {
                this.notificationFactory.showCommon('warning', 'upgrade.videoQualityHD');
            }
            return;
        }
        if (!this.isPatient) {
            this.User.prototype$updateAttributes({ id: this.User.getCurrentId() }, { videoQuality: quality });
        }
        void this.VideoQualityService.switchTo(quality);
    }
    isAudioTypeChangeAvailable() {
        if (!Boolean(this.currentUserHelper.user)) {
            return false;
        }
        if (!this.VideoProviderFactory.activeProvider.isInCall) {
            return false;
        }
        return this.currentUserHelper.user.hasAccess(ACCESS_TYPES.MEDICAL_AUDIO);
    }
    restartCall() {
        this.$ngRedux.dispatch(call.actions.restartCall(EndCallReasons.RESTART_FROM_SETTINGS));
    }
    toggleSpeakerTest() {
        if (!this.isSpeakersTesting) {
            this.isSpeakersTesting = true;
            void this.$timeout(() => {
                this.isSpeakersTesting = false;
            }, 5000);
            this.notificationFactory.playSound('waitingfor');
        }
    }
    canChangeVideoQuality() {
        if (!this.VideoProviderFactory.activeProvider.isInCall) {
            return false;
        }
        return this.videoSettingsHelper.canChangeVideoQuality(this.isPatient);
    }
    canChangeSpeakers() {
        return this.hasSpeakerAccess !== undefined && !this.Browser.isMobile;
    }
    speakersPlaceholder() {
        if (!this.setSinkId.isSupported) {
            return this.$translate.instant('video-settings.speakers-default');
        }
        return !this.hasSpeakerAccess
            ? this.$translate.instant('video-settings.not-enabled')
            : this.$translate.instant('video-settings.no-devices');
    }
    get isRestartCallEnabled() {
        return this.VideoProviderFactory.activeProvider.isInCall && !this.isPatient;
    }
    mapStateToThis(state) {
        return {
            hasMicrophoneAccess: devices.selectors.hasMicrophoneAccess(state),
            hasCameraAccess: devices.selectors.hasCameraAccess(state),
            hasSpeakerAccess: devices.selectors.hasSpeakerAccess(state),
            audioOptions: devices.selectors.getMicrophoneOptions(state),
            speakersOptions: devices.selectors.getSpeakerOptions(state),
            videoOptions: devices.selectors.getCameraOptions(state),
            accessibleMicrophones: devices.selectors.getAccessibleMicrophones(state),
            accessibleCameras: devices.selectors.getAccessibleCameras(state),
            accessibleSpeakers: devices.selectors.getAccessibleSpeakers(state),
            currentCamera: devices.selectors.getCurrentCamera(state),
            currentMic: devices.selectors.getCurrentMicrophone(state),
            currentSpeaker: devices.selectors.getCurrentSpeaker(state),
            hasVideo: localMedia.selectors.hasVideo(state),
            hasAudio: localMedia.selectors.hasAudio(state),
        };
    }
    handleAudioTypeChange(audioType) {
        this.$rootScope.$emit(RootScopeEvents.CHANGE_AUDIO_TYPE, audioType);
        if (this.VideoProviderFactory.activeProvider.audioType ===
            AudioTypes.MEDICAL &&
            this.VideoProviderFactory.activeProvider.isInCall) {
            this.communicationControl.switchToRoutedForAudioType();
        }
    }
}
const videoSettings = {
    template,
    bindings: {
        isPatient: '<',
    },
    controller: VideoSettingsController,
};
export default angular
    .module('doxy.common.components.videoSettings', [])
    // video-settings
    .component('videoSettings', videoSettings).name;
